/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: tenantsActions.ts
 🗓️ Created: 2026-05-02
 🧩 Description:
    Implementation of the tenants module.
────────────────────────────────────────────────────────────────*/

import type { NavigateFunction } from 'react-router-dom';
import { buildApiUrl, getApiHeaders, API_CONFIG } from '../../config/api';

/**
 * Shared helpers for Tenants module actions (used in Grid + Form views).
 */

/**
 * Build the URL used to duplicate a Tenant in the form.
 * Pattern: /admin/multi-tenant/tenants/new?duplicate={id}
 */
export const getTenantDuplicateUrl = (id: string | number): string =>
  `/admin/multi-tenant/tenants/new?duplicate=${id}`;

/**
 * Navigate to the form in duplicate mode for the given Tenant id.
 */
export const navigateToTenantDuplicate = (
  navigate: NavigateFunction,
  id: string | number,
): void => {
  navigate(getTenantDuplicateUrl(id));
};

/**
 * Low-level API helper to delete a Tenant by id.
 * UI components are responsible for showing notifications and handling navigation/refresh.
 */
export const deleteTenantById = async (
  id: string | number,
  tenantSlug: string,
  userId?: string | number | null,
): Promise<void> => {
  const response = await fetch(
    buildApiUrl(`${API_CONFIG.ENDPOINTS.TENANTS}/${id}`),
    {
      method: 'DELETE',
      headers: getApiHeaders(tenantSlug, userId),
    },
  );

  if (!response.ok) {
    let message = 'Failed to delete tenant';
    try {
      const errorData = await response.json();
      if (errorData?.message) {
        message = errorData.message;
      }
    } catch {
      // Ignore JSON parse errors and use default message
    }
    throw new Error(message);
  }
};

